import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { CompanyContext } from './CompanyContext';
import { useAuth } from './AuthContext';
import { buildApiUrl } from '../utils/api';
import { logger } from '../utils/logger';

const TrialContext = createContext();

export const TrialProvider = ({ children }) => {
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialData, setTrialData] = useState(null);
  // Gate leve: e-mail não verificado em trial NÃO bloqueia — só sinaliza (banner + trava ações-chave).
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedCompany } = useContext(CompanyContext);
  const { user } = useAuth();

  const checkTrialStatus = async (companyId) => {
    try {
      logger.log('Verificando status do trial para empresa:', companyId);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser || !companyId) {
        setIsLoading(false);
        return;
      }

      const idToken = await currentUser.getIdToken();
      
      logger.log('Fazendo requisição para verificar trial');
      const response = await fetch(
        buildApiUrl(`/api/check-trial/${companyId}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );

      logger.log('Resposta do check-trial:', response.status);
      const data = await response.json();
      logger.log('Dados do check-trial:', data);

      // 402 + code=PENDING_CHECKOUT → empresa criada mas checkout não foi concluído.
      if (response.status === 402 && data.code === 'PENDING_CHECKOUT') {
        logger.log('Checkout pendente detectado');
        setIsTrialExpired(true);
        setTrialData({
          userEmail: currentUser.email,
          subscriptionPlan: data.subscriptionPlan || 'atendente',
          type: 'pending_checkout',
          billingPortalAvailable: !!data.billingPortalAvailable,
        });
        setEmailNotVerified(false);
      } else if (response.status === 403 && data.code === 'SUBSCRIPTION_ENDED') {
        // Ex-assinante: cancelou (ou inadimpliu) e o ciclo pago terminou. Não é trial.
        logger.log('Assinatura encerrada detectada');
        setIsTrialExpired(true);
        setTrialData({
          userEmail: currentUser.email,
          subscriptionPlan: data.subscriptionPlan || 'especialista',
          type: 'subscription_ended',
          billingPortalAvailable: !!data.billingPortalAvailable,
        });
        setEmailNotVerified(false);
      } else if (data.code === 'TRIAL_EXPIRED' || (data.message && data.message.includes('período de avaliação expirou'))) {
        logger.log('Trial expirado detectado');
        setIsTrialExpired(true);
        setTrialData({
          userEmail: currentUser.email,
          subscriptionPlan: data.subscriptionPlan || 'especialista',
          type: 'trial',
          billingPortalAvailable: !!data.billingPortalAvailable,
        });
        setEmailNotVerified(false);
      } else {
        logger.log('Trial válido ou outro status');
        setIsTrialExpired(false);
        setTrialData(null);
        // Gate leve: banner de "confirme seu e-mail" só quando está EM TRIAL e o e-mail
        // ainda não foi verificado. Não bloqueia o acesso — só sinaliza.
        setEmailNotVerified(data.subscriptionStatus === 'trial' && data.emailVerified === false);
      }
    } catch (error) {
      console.error('Erro ao verificar status do trial:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Re-roda quando a empresa OU o usuário logado mudar (login após logout com mesma empresa
  // no localStorage não alterava companyId e o check-trial era ignorado antes).
  useEffect(() => {
    const companyId = selectedCompany?.companyId;
    if (companyId && user?.uid) {
      logger.log('Empresa selecionada, verificando status do trial:', companyId);
      setIsLoading(true);
      checkTrialStatus(companyId);
    } else {
      setIsTrialExpired(false);
      setTrialData(null);
      setEmailNotVerified(false);
      setIsLoading(false);
    }
  }, [selectedCompany?.companyId, user?.uid]);

  return (
    <TrialContext.Provider value={{
      isTrialExpired,
      trialData,
      isLoading,
      checkTrialStatus,
      emailNotVerified,
      setEmailNotVerified,
    }}>
      {children}
    </TrialContext.Provider>
  );
};

export const useTrial = () => {
  const context = useContext(TrialContext);
  if (!context) {
    throw new Error('useTrial deve ser usado dentro de um TrialProvider');
  }
  return context;
}; 